'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import PinVerificationModal from './PinVerificationModal';

const LoginForm = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAdminUserId, setPendingAdminUserId] = useState<string | null>(null);

  useEffect(() => {
    const invitedEmail = searchParams.get('email');
    if (invitedEmail) setEmail(invitedEmail);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);

      // Fetch user profile to check role
      const supabase = createClient();
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role, admin_pin')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          // If profile doesn't exist, redirect to dashboard anyway
          router.push('/dashboard');
          return;
        }

        // If admin user, show PIN verification modal
        if (profile?.role === 'admin') {
          if (profile.admin_pin) {
            setPendingAdminUserId(user.id);
            setShowPinModal(true);
            setLoading(false);
            return;
          } else {
            // Admin without PIN - allow access but show warning
            console.warn('Admin user without PIN configured');
            router.push('/admin');
          }
        } else {
          // Regular user - redirect to returnTo or dashboard
          router.push(returnTo || '/dashboard');
        }
      } else {
        router.push(returnTo || '/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinVerified = () => {
    setShowPinModal(false);
    setPendingAdminUserId(null);
    router.push('/admin');
  };

  const handlePinCancel = async () => {
    setShowPinModal(false);
    setPendingAdminUserId(null);

    // Sign out the user since they cancelled PIN verification
    const supabase = createClient();
    await supabase.auth.signOut();

    setError('Admin login cancelled. PIN verification is required.');
  };

  return (
    <>
      <div className="w-full max-w-md bg-card rounded-3xl border border-border p-8 md:p-10 shadow-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-jakarta font-bold text-3xl text-foreground mb-3">Welcome Back</h1>
          <p className="text-muted-foreground">Access your messages and memorial content</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-xl text-error text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 pl-12 bg-input border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground"
                required
              />

              <Icon
                name="EnvelopeIcon"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 pl-12 pr-12 bg-input border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground"
                required
              />

              <Icon
                name="LockClosedIcon"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-ring"
              />

              <span className="text-sm text-muted-foreground">Remember me</span>
            </label>
            <Link
              href="/forgot-password"
              className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            >
              Forgot password?
            </Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Log In'}
            {!loading && (
              <Icon
                name="ArrowRightIcon"
                size={20}
                className="group-hover:translate-x-1 transition-transform"
              />
            )}
          </button>
        </form>

        {/* Security Indicators */}
        <div className="mt-8 pt-6 border-t border-border">
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="LockClosedIcon" size={16} className="text-success" />
              <span>256-bit Encryption</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Icon name="ShieldCheckIcon" size={16} className="text-success" />
              <span>Secure Login</span>
            </div>
          </div>
        </div>

        {/* Sign Up Link */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm">
            Don&apos;t have an account?{' '}
            <Link
              href="/signup"
              className="text-primary hover:text-primary/80 font-semibold transition-colors"
            >
              Create Free Account
            </Link>
          </p>
        </div>
      </div>

      {/* PIN Verification Modal */}
      {pendingAdminUserId && (
        <PinVerificationModal
          isOpen={showPinModal}
          onVerify={handlePinVerified}
          onCancel={handlePinCancel}
          userId={pendingAdminUserId}
        />
      )}
    </>
  );
};

export default LoginForm;
